import { collection, addDoc, updateDoc, doc, getDocs, deleteDoc, query, where, getDoc, writeBatch, runTransaction } from "firebase/firestore";
import { db, LEGACY_TENANT_ID } from "@/lib/firebase";
import { Lead } from "@/components/lead-card";
import { toast } from "@/hooks/use-toast";
import { createLead, fetchLeads, LEADS_COLLECTION } from "./leadsService";
import { logger } from "@/lib/logger";
import { cache } from "@/lib/cache";

// Authoritative audit-trail collection for every lead assignment event.
export const LEAD_ASSIGNMENTS_COLLECTION = "leadAssignments";

export type LeadAssignmentType =
  | "order"     // assignLeadsToOrder (paid via leadOrders)
  | "direct"    // handleAssignLead (admin one-off)
  | "bulk"      // handleBulkAssignLeads (all leads of a folder)
  | "count"     // handleCountAssignLeads (N from folder)
  | "selected"  // handleBulkAssignSelectedLeads (checkbox selection)
  | "purchase"  // purchaseLead (consultant self-buy)
  | "package";  // purchaseLeadPackage

export interface LeadAssignment {
  id: string;                  // === recipientLeadId for hard 1:1 join
  createdAt: string;
  sourceMarketLeadId: string;
  recipientLeadId: string;
  recipientUserId: string;
  assignmentType: LeadAssignmentType;
  assignedByUserId: string;    // for self-purchase, equals recipientUserId
  paid: boolean;
  sellerId: string;
  sellerName: string;
  tenantId: string;
  orderId?: string;
  folderName?: string;
  sourceLeadId?: string;       // original seller-side lead id (provenance)
  packageId?: string;
  packageSize?: number;
  cleanupStatus?: "active" | "duplicate_removed";
  cleanupRunId?: string;
  notes?: string;
}

// Interface for market lead listings
export interface MarketLead {
  id: string;
  leadId: string;
  leadData: Lead;
  price: number;
  description: string;
  folderName: string;
  sellerName: string;
  sellerId: string;
  listedDate: string;
  isAvailable: boolean;
  type: "new" | "old-contact"; // New field to distinguish between new leads and old contacts
  followUpDate?: string; // For old contacts, when they can be reassigned
  assignedTo?: string; // For leads that are assigned to a consultant
  status: "available" | "assigned" | "sold" | "pending"; // New status field
  packageSize?: number; // New field for folder package size
  isPackage?: boolean; // New field to indicate if this is a folder package
  packageLeads?: string[]; // Array of lead IDs included in the package
  tenantId?: string;
}

// Interface for lead orders
export interface LeadOrder {
  id: string;
  userId: string;
  userName: string;
  requestedAmount: number;
  averagePrice: number;
  totalPrice: number;
  status: "pending" | "approved" | "rejected" | "completed";
  createdAt: string;
  updatedAt: string;
  assignedLeads?: string[]; // Array of lead IDs that were assigned
  notes?: string;
  folderName?: string; // Optional, da es auch individuelle Anfragen ohne Ordner geben kann
  tenantId?: string;
}

// Interface for lead packages
export interface LeadPackage {
  id: string;
  folderName: string;
  sellerName: string;
  sellerId: string;
  listedDate: string;
  description: string;
  status: "available" | "sold" | "pending";
  packages: {
    size: number;
    price: number;
    availableLeads: number;
  }[];
  totalLeads: number;
  averagePrice: number;
  tenantId?: string;
}

// Export the collection name constant
export const MARKET_LEADS_COLLECTION = "marketLeads";

// Collection names
export const LEAD_ORDERS_COLLECTION = "leadOrders";
const OLD_CONTACTS_COLLECTION = "oldContacts";

// Firestore rejects undefined field values, and Lead objects built by
// convertFirestoreData explicitly carry undefined for missing optional
// fields (source, company, profession, ...). Strip undefined values
// recursively before nesting a lead into a document. Date objects and
// Firestore Timestamps are preserved as-is.
const stripUndefined = (value: any): any => {
  if (Array.isArray(value)) {
    return value.map(stripUndefined);
  }
  if (
    value !== null &&
    typeof value === "object" &&
    !(value instanceof Date) &&
    typeof value.toDate !== "function"
  ) {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, stripUndefined(v)])
    );
  }
  return value;
};

// Fetch all available market leads and old contacts
export const fetchMarketLeads = async (
  tenantId: string = LEGACY_TENANT_ID,
  skipCache: boolean = false
): Promise<{
  newLeads: MarketLead[],
  oldContacts: MarketLead[],
  packages: LeadPackage[]
}> => {
  try {
    const cacheKey = `marketLeads:${tenantId}`;

    // Check cache first
    if (!skipCache) {
      const cached = cache.get<{
        newLeads: MarketLead[],
        oldContacts: MarketLead[],
        packages: LeadPackage[]
      }>(cacheKey);
      if (cached) {
        logger.log(`[Cache HIT] marketLeads for tenant ${tenantId}`);
        return cached;
      }
    }

    logger.log(`[Cache MISS] Fetching marketLeads for tenant ${tenantId}`);

    // Fetch market leads from marketplace with server-side filtering
    // This requires all documents to have a tenantId field
    // Run the migration script (scripts/migrate-tenant-ids.ts) to add tenantId to existing documents
    const marketLeadsRef = collection(db, MARKET_LEADS_COLLECTION);
    const q = query(marketLeadsRef, where("tenantId", "==", tenantId));

    const marketLeadsQuery = await getDocs(q);
    const marketLeads = marketLeadsQuery.docs.map(doc => {
      const data = doc.data() as Omit<MarketLead, 'id'>;
      return {
        id: doc.id,
        ...data,
        tenantId: data.tenantId || LEGACY_TENANT_ID
      } as MarketLead;
    });

    // Separate packages, new leads, and old contacts
    const packages = marketLeads.filter(lead => lead.id.startsWith('package-')) as unknown as LeadPackage[];
    const newLeads = marketLeads.filter(lead => !lead.id.startsWith('package-') && lead.type === "new");
    const marketplaceOldContacts = marketLeads.filter(lead => !lead.id.startsWith('package-') && lead.type === "old-contact");

    const result = {
      newLeads,
      oldContacts: marketplaceOldContacts,
      packages
    };

    // Cache the result for 10 minutes
    cache.set(cacheKey, result);

    return result;
  } catch (error) {
    logger.error("Error fetching market leads:", error);
    toast({
      title: "Fehler beim Laden der Leads",
      description: "Die Leads konnten nicht geladen werden.",
      variant: "destructive"
    });
    return {
      newLeads: [],
      oldContacts: [],
      packages: []
    };
  }
};

// Fetch a specific market lead by ID
export const getMarketLeadById = async (id: string): Promise<MarketLead | null> => {
  try {
    const docRef = doc(db, MARKET_LEADS_COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data() as Omit<MarketLead, 'id'>;
      return {
        id: docSnap.id,
        ...data,
        tenantId: data.tenantId || LEGACY_TENANT_ID
      } as MarketLead;
    } else {
      return null;
    }
  } catch (error) {
    logger.error("Error fetching market lead:", error);
    return null;
  }
};

// List a lead for sale on the marketplace
export const createMarketListing = async (
  leadId: string,
  leadData: Lead,
  price: number,
  description: string,
  folderName: string,
  sellerName: string,
  sellerId: string,
  tenantId: string = LEGACY_TENANT_ID
): Promise<string | null> => {
  try {
    const marketLead: Omit<MarketLead, "id"> = {
      leadId,
      leadData: stripUndefined(leadData),
      price,
      description,
      folderName,
      sellerName,
      sellerId,
      listedDate: new Date().toISOString(),
      isAvailable: true,
      type: "new",
      status: "available",
      tenantId
    };

    const docRef = await addDoc(collection(db, MARKET_LEADS_COLLECTION), marketLead);

    // Invalidate cache for this tenant
    cache.invalidate(`marketLeads:${tenantId}`);

    toast({
      title: "Lead zum Verkauf eingestellt",
      description: "Ihr Lead wurde erfolgreich auf dem Marktplatz eingestellt."
    });

    return docRef.id;
  } catch (error) {
    logger.error("Error creating market listing:", error);
    toast({
      title: "Fehler",
      description: "Der Lead konnte nicht zum Verkauf eingestellt werden.",
      variant: "destructive"
    });
    return null;
  }
};

// Purchase a lead from the marketplace
export const purchaseLead = async (marketLeadId: string, buyerId: string): Promise<boolean> => {
  try {
    const marketLeadRef = doc(db, MARKET_LEADS_COLLECTION, marketLeadId);
    const newLeadRef = doc(collection(db, LEADS_COLLECTION));
    let tenantIdForCache = LEGACY_TENANT_ID;

    await runTransaction(db, async (tx) => {
      const snap = await tx.get(marketLeadRef);
      if (!snap.exists()) throw new Error("Lead nicht mehr verfügbar.");
      const marketLead = { id: snap.id, ...snap.data() } as MarketLead;
      if (!marketLead.isAvailable || marketLead.status !== "available") {
        throw new Error("Lead nicht mehr verfügbar.");
      }
      tenantIdForCache = marketLead.tenantId || LEGACY_TENANT_ID;

      const currentDate = new Date().toLocaleDateString('de-DE');
      const marketplacePurchaseInfo = `Über Marktplatz erworben von ${marketLead.sellerName} am ${currentDate}`;
      const newLeadData = {
        ...marketLead.leadData,
        id: newLeadRef.id,
        assignedTo: buyerId,
        folderId: null,
        source: "market",
        marketLeadId: marketLead.id,
        marketplacePurchaseInfo,
        notes: "",
        createdAt: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        nextContactDate: marketLead.leadData.nextContactDate ||
          (marketLead.type === "old-contact" && marketLead.followUpDate
            ? (marketLead.followUpDate as unknown as { __time__?: string }).__time__ || marketLead.followUpDate
            : marketLead.leadData.nextContactDate),
        tenantId: marketLead.tenantId || LEGACY_TENANT_ID,
        history: [{ type: "created", date: new Date().toISOString(), user: buyerId, changes: [] }],
      };

      tx.set(newLeadRef, newLeadData);
      tx.set(doc(db, LEAD_ASSIGNMENTS_COLLECTION, newLeadRef.id), {
        id: newLeadRef.id,
        createdAt: new Date().toISOString(),
        sourceMarketLeadId: marketLead.id,
        recipientLeadId: newLeadRef.id,
        recipientUserId: buyerId,
        assignmentType: "purchase",
        assignedByUserId: buyerId,
        paid: true,
        sellerId: marketLead.sellerId,
        sellerName: marketLead.sellerName,
        tenantId: marketLead.tenantId || LEGACY_TENANT_ID,
        folderName: marketLead.folderName,
        sourceLeadId: marketLead.leadId,
        cleanupStatus: "active",
      });
      tx.delete(marketLeadRef);
    });

    cache.invalidate(`marketLeads:${tenantIdForCache}`);
    toast({ title: "Lead gekauft", description: "Der Lead wurde erfolgreich gekauft und zu Ihrer Lead-Liste hinzugefügt." });
    return true;
  } catch (error) {
    logger.error("Error purchasing lead:", error);
    const msg = error instanceof Error ? error.message : "Beim Kauf des Leads ist ein Fehler aufgetreten.";
    toast({ title: "Fehler", description: msg, variant: "destructive" });
    return false;
  }
};

// Remove a listing from the marketplace
export const deleteMarketListing = async (
  id: string,
  tenantId: string = LEGACY_TENANT_ID
): Promise<boolean> => {
  try {
    await deleteDoc(doc(db, MARKET_LEADS_COLLECTION, id));

    // Invalidate cache for this tenant
    cache.invalidate(`marketLeads:${tenantId}`);

    return true;
  } catch (error) {
    logger.error("Error deleting market listing:", error);
    return false;
  }
};

// Get all listings by a seller
export const getSellerListings = async (
  sellerId: string,
  tenantId: string = LEGACY_TENANT_ID
): Promise<MarketLead[]> => {
  try {
    const leadsRef = collection(db, MARKET_LEADS_COLLECTION);
    const q = query(
      leadsRef,
      where("sellerId", "==", sellerId),
      where("tenantId", "==", tenantId)
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => {
      const data = doc.data() as Omit<MarketLead, 'id'>;
      return {
        id: doc.id,
        ...data,
        tenantId: data.tenantId || LEGACY_TENANT_ID
      } as MarketLead;
    });
  } catch (error) {
    logger.error("Error fetching seller listings:", error);
    return [];
  }
};

// New function to create a lead order
export const createLeadOrder = async (
  userId: string,
  userName: string,
  requestedAmount: number,
  averagePrice: number,
  folderName?: string, // Optional parameter for folder name
  tenantId: string = LEGACY_TENANT_ID
): Promise<string | null> => {
  try {
    const order: Omit<LeadOrder, "id"> = {
      userId,
      userName,
      requestedAmount,
      averagePrice,
      totalPrice: requestedAmount * averagePrice,
      status: "pending",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      folderName, // Add folder name to order
      tenantId
    };

    const docRef = await addDoc(collection(db, LEAD_ORDERS_COLLECTION), order);
    toast({
      title: "Lead-Order erstellt",
      description: folderName
        ? `Ihre Lead-Order für ${requestedAmount} Leads aus dem Ordner "${folderName}" wurde erstellt und wird von einem Admin geprüft.`
        : "Ihre Lead-Order wurde erfolgreich erstellt und wird von einem Admin geprüft."
    });

    return docRef.id;
  } catch (error) {
    logger.error("Error creating lead order:", error);
    toast({
      title: "Fehler",
      description: "Die Lead-Order konnte nicht erstellt werden.",
      variant: "destructive"
    });
    return null;
  }
};

// New function to get all pending lead orders
export const getPendingLeadOrders = async (tenantId: string = LEGACY_TENANT_ID): Promise<LeadOrder[]> => {
  try {
    const ordersRef = collection(db, LEAD_ORDERS_COLLECTION);

    // Always use composite WHERE clause for server-side filtering
    const q = query(
      ordersRef,
      where("status", "==", "pending"),
      where("tenantId", "==", tenantId)
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => {
      const data = doc.data() as Omit<LeadOrder, 'id'>;
      return {
        id: doc.id,
        ...data,
        tenantId: data.tenantId || LEGACY_TENANT_ID
      } as LeadOrder;
    });
  } catch (error) {
    logger.error("Error fetching pending lead orders:", error);
    return [];
  }
};

// New function to update lead order status
export const updateLeadOrderStatus = async (
  orderId: string,
  status: LeadOrder["status"],
  assignedLeads?: string[],
  notes?: string
): Promise<boolean> => {
  try {
    const orderRef = doc(db, LEAD_ORDERS_COLLECTION, orderId);
    const updateData: Partial<LeadOrder> = {
      status,
      updatedAt: new Date().toISOString()
    };

    if (assignedLeads) {
      updateData.assignedLeads = assignedLeads;
    }

    if (notes) {
      updateData.notes = notes;
    }

    await updateDoc(orderRef, updateData);

    toast({
      title: "Lead-Order aktualisiert",
      description: `Die Lead-Order wurde auf "${status}" gesetzt.`
    });

    return true;
  } catch (error) {
    logger.error("Error updating lead order:", error);
    toast({
      title: "Fehler",
      description: "Die Lead-Order konnte nicht aktualisiert werden.",
      variant: "destructive"
    });
    return false;
  }
};

// New function to add leads to marketplace
export async function addLeadsToMarketplace(
  leads: Lead[],
  price: number,
  folderName: string,
  description: string,
  sellerId: string,
  sellerName: string,
  tenantId: string = LEGACY_TENANT_ID
): Promise<boolean> {
  try {
    const batch = writeBatch(db);

    leads.forEach(lead => {
      const marketLeadRef = doc(collection(db, MARKET_LEADS_COLLECTION));

      // Create base market lead data
      const marketLeadData: Omit<MarketLead, 'id'> = {
        leadId: lead.id,
        leadData: lead,
        price,
        description,
        folderName,
        sellerName,
        sellerId,
        listedDate: new Date().toISOString(),
        isAvailable: true,
        type: lead.status === "old-contact" ? "old-contact" : "new",
        status: "available",
        tenantId
      };

      // Only add followUpDate if it exists and is valid
      if (lead.nextContactDate && lead.status === "old-contact") {
        marketLeadData.followUpDate = lead.nextContactDate as string;
      }

      batch.set(marketLeadRef, marketLeadData);
    });

    await batch.commit();

    // Invalidate cache for this tenant
    cache.invalidate(`marketLeads:${tenantId}`);

    toast({
      title: "Leads hinzugefügt",
      description: `${leads.length} Leads wurden erfolgreich zur Börse hinzugefügt.`
    });

    return true;
  } catch (error) {
    logger.error("Error adding leads to marketplace:", error);
    toast({
      title: "Fehler beim Hinzufügen der Leads",
      description: "Die Leads konnten nicht zur Börse hinzugefügt werden.",
      variant: "destructive"
    });
    return false;
  }
}

// Maximum operations per Firestore transaction. We use 100 (≈ 300 ops:
// 1 read + 1 set + 1 delete per lead) to stay safely under the 500 limit.
const ORDER_ASSIGN_CHUNK = 100;

// Function to automatically assign leads to an approved order.
// Uses Firestore transactions per chunk to prevent concurrent approvals from
// re-assigning the same marketLeads to multiple recipients.
export const assignLeadsToOrder = async (
  orderId: string,
  order: LeadOrder
): Promise<boolean> => {
  try {
    // Get available leads matching the order criteria — folder-aware.
    // This read is a snapshot; the transaction below re-verifies each lead.
    const availableLeads = await getAvailableLeadsForOrder(
      order.requestedAmount,
      order.tenantId,
      order.folderName
    );

    if (availableLeads.length < order.requestedAmount) {
      const folderHint = order.folderName ? ` für den Ordner "${order.folderName}"` : "";
      const description = `Es sind nur ${availableLeads.length} von ${order.requestedAmount} benötigten Leads${folderHint} verfügbar.`;
      try {
        const orderRef = doc(db, LEAD_ORDERS_COLLECTION, orderId);
        const failureNote = `Automatische Zuweisung fehlgeschlagen am ${new Date().toLocaleDateString('de-DE')}: ${description}`;
        await updateDoc(orderRef, {
          notes: failureNote,
          updatedAt: new Date().toISOString()
        });
      } catch (noteErr) {
        logger.error("Could not persist failure note on order:", noteErr);
      }
      toast({ title: "Nicht genügend Leads verfügbar", description, variant: "destructive" });
      return false;
    }

    const currentDate = new Date().toLocaleDateString('de-DE');
    const assignedLeadIds: string[] = [];
    const skippedLeads: string[] = [];

    // Process in chunks; each chunk runs in one transaction that re-reads
    // every marketLead doc and aborts that doc if it has already been claimed.
    for (let start = 0; start < availableLeads.length; start += ORDER_ASSIGN_CHUNK) {
      const chunk = availableLeads.slice(start, start + ORDER_ASSIGN_CHUNK);

      // Local arrays scoped per chunk. Firestore may retry the tx callback on
      // contention, so we MUST not push to outer arrays inside the callback.
      // We re-init these on every callback invocation and only commit them
      // after the tx resolves successfully.
      let chunkAssignedIds: string[] = [];
      let chunkSkippedIds: string[] = [];

      await runTransaction(db, async (tx) => {
        chunkAssignedIds = [];
        chunkSkippedIds = [];

        const reads = await Promise.all(
          chunk.map(lead => tx.get(doc(db, MARKET_LEADS_COLLECTION, lead.id)))
        );

        for (let i = 0; i < chunk.length; i++) {
          const lead = chunk[i];
          const snap = reads[i];
          if (!snap.exists()) { chunkSkippedIds.push(lead.id); continue; }
          const data = snap.data() as MarketLead;
          if (data.status !== "available" || data.type !== "new") {
            chunkSkippedIds.push(lead.id);
            continue;
          }

          const newLeadRef = doc(collection(db, LEADS_COLLECTION));
          const marketplacePurchaseInfo = `Über Marktplatz erworben von ${lead.sellerName} am ${currentDate}`;
          const newLeadData = {
            ...lead.leadData,
            id: newLeadRef.id,
            folderId: null,
            assignedTo: order.userId,
            status: "new",
            createdAt: new Date().toISOString(),
            lastUpdated: new Date().toISOString(),
            nextContactDate: lead.type === "old-contact" && lead.followUpDate
              ? (lead.followUpDate as unknown as { __time__?: string }).__time__ || lead.followUpDate
              : lead.leadData.nextContactDate,
            source: "market",
            marketLeadId: lead.id,
            marketplacePurchaseInfo,
            notes: "",
            history: [{ type: "created", date: new Date().toISOString(), user: order.userId, changes: [] }],
            tenantId: lead.tenantId || LEGACY_TENANT_ID
          };

          tx.set(newLeadRef, newLeadData);
          tx.set(doc(db, LEAD_ASSIGNMENTS_COLLECTION, newLeadRef.id), {
            id: newLeadRef.id,
            createdAt: new Date().toISOString(),
            sourceMarketLeadId: lead.id,
            recipientLeadId: newLeadRef.id,
            recipientUserId: order.userId,
            assignmentType: "order",
            assignedByUserId: order.userId,
            paid: true,
            sellerId: lead.sellerId,
            sellerName: lead.sellerName,
            tenantId: lead.tenantId || LEGACY_TENANT_ID,
            orderId,
            folderName: order.folderName,
            sourceLeadId: lead.leadId,
            cleanupStatus: "active",
          });
          tx.delete(doc(db, MARKET_LEADS_COLLECTION, lead.id));
          chunkAssignedIds.push(lead.id);
        }
      });

      assignedLeadIds.push(...chunkAssignedIds);
      skippedLeads.push(...chunkSkippedIds);
    }

    // If anything was skipped (concurrent claim), abort with a clear note.
    if (assignedLeadIds.length < order.requestedAmount) {
      const description = `Race-Condition erkannt: nur ${assignedLeadIds.length} von ${order.requestedAmount} Leads konnten reserviert werden. ${skippedLeads.length} wurden bereits anderweitig zugewiesen.`;
      const orderRef = doc(db, LEAD_ORDERS_COLLECTION, orderId);
      await updateDoc(orderRef, {
        notes: `Teilzuweisung am ${new Date().toLocaleDateString('de-DE')}: ${description}`,
        assignedLeads: assignedLeadIds,
        updatedAt: new Date().toISOString()
      });
      cache.invalidate(`marketLeads:${order.tenantId || LEGACY_TENANT_ID}`);
      toast({ title: "Teilzuweisung", description, variant: "destructive" });
      return false;
    }

    // Mark order as completed.
    await updateDoc(doc(db, LEAD_ORDERS_COLLECTION, orderId), {
      status: "completed",
      assignedLeads: assignedLeadIds,
      notes: "",
      updatedAt: new Date().toISOString()
    });

    cache.invalidate(`marketLeads:${order.tenantId || LEGACY_TENANT_ID}`);
    toast({ title: "Leads zugewiesen", description: `${assignedLeadIds.length} Leads wurden erfolgreich zugewiesen.` });
    return true;
  } catch (error) {
    logger.error("Error assigning leads:", error);
    toast({ title: "Fehler bei der Zuweisung", description: "Die Leads konnten nicht zugewiesen werden.", variant: "destructive" });
    return false;
  }
};

// Helper function to get available leads for an order
const getAvailableLeadsForOrder = async (
  amount: number,
  tenantId: string = LEGACY_TENANT_ID,
  folderName?: string
): Promise<MarketLead[]> => {
  try {
    const leadsRef = collection(db, MARKET_LEADS_COLLECTION);

    // Always use composite WHERE clause for server-side filtering
    const q = query(
      leadsRef,
      where("status", "==", "available"),
      where("type", "==", "new"),
      where("tenantId", "==", tenantId)
    );

    const querySnapshot = await getDocs(q);

    const all = querySnapshot.docs.map(doc => {
      const data = doc.data() as Omit<MarketLead, 'id'>;
      return { id: doc.id, ...data, tenantId: data.tenantId || LEGACY_TENANT_ID } as MarketLead;
    });

    const filtered = folderName
      ? all.filter(l => l.folderName === folderName)
      : all;

    return filtered
      .sort((a, b) => new Date(a.listedDate).getTime() - new Date(b.listedDate).getTime())
      .slice(0, amount);
  } catch (error) {
    logger.error("Error getting available leads:", error);
    return [];
  }
};

// New function to create a folder package
export const createFolderPackage = async (
  folderName: string,
  description: string,
  sellerId: string,
  sellerName: string,
  leads: MarketLead[],
  tenantId: string = LEGACY_TENANT_ID
): Promise<string | null> => {
  try {
    // Calculate average price
    const averagePrice = leads.reduce((sum, lead) => sum + lead.price, 0) / leads.length;

    // Create package sizes (5, 10, 20 leads)
    const packageSizes = [5, 10, 20];
    const packages = packageSizes.map(size => ({
      size,
      price: Math.round(averagePrice * size),
      availableLeads: Math.min(size, leads.length)
    }));

    const packageData: LeadPackage = {
      id: `package-${Date.now()}`,
      folderName,
      sellerName,
      sellerId,
      listedDate: new Date().toISOString(),
      description,
      status: "available",
      packages,
      totalLeads: leads.length,
      averagePrice,
      tenantId
    };

    // Create a batch for the updates
    const batch = writeBatch(db);

    // Add the package document
    const packageRef = doc(collection(db, MARKET_LEADS_COLLECTION), packageData.id);
    batch.set(packageRef, packageData);

    // Update all leads to be part of this package
    leads.forEach(lead => {
      const leadRef = doc(db, MARKET_LEADS_COLLECTION, lead.id);
      batch.update(leadRef, {
        isPackage: true,
        packageSize: null, // Will be set when purchased
        packageLeads: leads.map(l => l.id),
        status: "pending" // Mark as pending until package is purchased
      });
    });

    await batch.commit();

    // Invalidate cache for this tenant
    cache.invalidate(`marketLeads:${tenantId}`);

    toast({
      title: "Lead-Paket erstellt",
      description: `Das Lead-Paket "${folderName}" wurde erfolgreich erstellt.`
    });

    return packageData.id;
  } catch (error) {
    logger.error("Error creating folder package:", error);
    toast({
      title: "Fehler",
      description: "Das Lead-Paket konnte nicht erstellt werden.",
      variant: "destructive"
    });
    return null;
  }
};

// New function to purchase a lead package
export const purchaseLeadPackage = async (
  packageId: string,
  packageSize: number,
  buyerId: string
): Promise<boolean> => {
  try {
    // Get the package
    const packageRef = doc(db, MARKET_LEADS_COLLECTION, packageId);
    const packageDoc = await getDoc(packageRef);

    if (!packageDoc.exists()) {
      toast({
        title: "Paket nicht gefunden",
        description: "Das ausgewählte Lead-Paket existiert nicht mehr.",
        variant: "destructive"
      });
      return false;
    }

    const packageData = packageDoc.data() as LeadPackage;
    const selectedPackage = packageData.packages.find(p => p.size === packageSize);

    if (!selectedPackage || selectedPackage.availableLeads < packageSize) {
      toast({
        title: "Paket nicht verfügbar",
        description: "Das ausgewählte Lead-Paket ist nicht mehr verfügbar.",
        variant: "destructive"
      });
      return false;
    }

    // Get all leads in the package (initial snapshot for IDs; re-read in tx).
    const leadsRef = collection(db, MARKET_LEADS_COLLECTION);
    const q = query(leadsRef, where("packageLeads", "array-contains", packageId));
    const leadsSnapshot = await getDocs(q);
    const candidateLeads = leadsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as MarketLead));

    if (candidateLeads.length < packageSize) {
      toast({
        title: "Paket nicht verfügbar",
        description: "Es sind nicht mehr genug Leads im Paket verfügbar.",
        variant: "destructive"
      });
      return false;
    }

    const candidates = candidateLeads.slice(0, packageSize);
    const currentDate = new Date().toLocaleDateString('de-DE');
    let assignedCount = 0;
    let tenantIdForCache = packageData.tenantId || LEGACY_TENANT_ID;

    // Single transaction: re-read each lead + the package doc, write atomically.
    // Package sizes are bounded by UI (5/10/20), well under the 500-op limit.
    await runTransaction(db, async (tx) => {
      assignedCount = 0;

      const pkgSnap = await tx.get(packageRef);
      if (!pkgSnap.exists()) throw new Error("Paket existiert nicht mehr.");
      const pkgData = pkgSnap.data() as LeadPackage;
      const pkgSelected = pkgData.packages.find(p => p.size === packageSize);
      if (!pkgSelected || pkgSelected.availableLeads < packageSize) {
        throw new Error("Paket nicht mehr verfügbar.");
      }
      tenantIdForCache = pkgData.tenantId || LEGACY_TENANT_ID;

      const leadReads = await Promise.all(
        candidates.map(lead => tx.get(doc(db, MARKET_LEADS_COLLECTION, lead.id)))
      );

      for (let i = 0; i < candidates.length; i++) {
        const lead = candidates[i];
        const snap = leadReads[i];
        if (!snap.exists()) continue;
        const data = snap.data() as MarketLead;
        if (data.status === "sold") continue;

        const marketplacePurchaseInfo = `Über Marktplatz erworben von ${pkgData.sellerName} am ${currentDate} (Paket: ${pkgData.folderName})`;
        const newLeadRef = doc(collection(db, LEADS_COLLECTION));
        const newLeadData = {
          ...lead.leadData,
          id: newLeadRef.id,
          assignedTo: buyerId,
          folderId: null,
          status: "new",
          createdAt: new Date().toISOString(),
          lastUpdated: new Date().toISOString(),
          nextContactDate: lead.type === "old-contact" && lead.followUpDate
            ? (lead.followUpDate as unknown as { __time__?: string }).__time__ || lead.followUpDate
            : null,
          source: "market",
          marketLeadId: lead.id,
          marketplacePurchaseInfo,
          history: [{ type: "created", date: new Date().toISOString(), user: buyerId, changes: [] }],
          tenantId: lead.tenantId || LEGACY_TENANT_ID
        };

        tx.set(newLeadRef, newLeadData);
        tx.set(doc(db, LEAD_ASSIGNMENTS_COLLECTION, newLeadRef.id), {
          id: newLeadRef.id,
          createdAt: new Date().toISOString(),
          sourceMarketLeadId: lead.id,
          recipientLeadId: newLeadRef.id,
          recipientUserId: buyerId,
          assignmentType: "package",
          assignedByUserId: buyerId,
          paid: true,
          sellerId: pkgData.sellerId,
          sellerName: pkgData.sellerName,
          tenantId: lead.tenantId || LEGACY_TENANT_ID,
          packageId,
          packageSize,
          folderName: pkgData.folderName,
          sourceLeadId: lead.leadId,
          cleanupStatus: "active",
        });
        tx.delete(doc(db, MARKET_LEADS_COLLECTION, lead.id));
        assignedCount++;
      }

      if (assignedCount < packageSize) {
        throw new Error(`Race-Condition: nur ${assignedCount} von ${packageSize} Leads waren noch verfügbar.`);
      }

      const updatedPackages = pkgData.packages.map(p =>
        p.size === packageSize ? { ...p, availableLeads: p.availableLeads - packageSize } : p
      );
      const allSold = updatedPackages.every(p => p.availableLeads === 0);
      tx.update(packageRef, { packages: updatedPackages, status: allSold ? "sold" : "available" });
    });

    cache.invalidate(`marketLeads:${tenantIdForCache}`);

    toast({
      title: "Lead-Paket gekauft",
      description: `Das Lead-Paket "${packageData.folderName}" wurde erfolgreich gekauft.`
    });

    return true;
  } catch (error) {
    logger.error("Error purchasing lead package:", error);
    toast({
      title: "Fehler",
      description: "Das Lead-Paket konnte nicht gekauft werden.",
      variant: "destructive"
    });
    return false;
  }
};

// Add new function to add an old contact to the marketplace
export const addOldContactToMarketplace = async (
  lead: Lead,
  sellerId: string,
  sellerName: string,
  price: number = 0,
  description: string = "Altkontakt",
  folderName: string = "Altkontakte",
  tenantId: string = LEGACY_TENANT_ID
): Promise<string | null> => {
  try {
    // Create a new market lead document
    const marketLeadRef = await addDoc(collection(db, MARKET_LEADS_COLLECTION), {
      leadId: lead.id,
      leadData: stripUndefined(lead),
      price,
      description,
      folderName,
      sellerName,
      sellerId,
      listedDate: new Date().toISOString(),
      isAvailable: true,
      type: "old-contact",
      status: "available",
      followUpDate: lead.nextContactDate || null,
      tenantId
    });

    // Invalidate cache for this tenant
    cache.invalidate(`marketLeads:${tenantId}`);

    return marketLeadRef.id;
  } catch (error) {
    logger.error("Error adding old contact to marketplace:", error);
    toast({
      title: "Fehler",
      description: "Der Altkontakt konnte nicht zur Leadbörse hinzugefügt werden.",
      variant: "destructive"
    });
    return null;
  }
};
